// STK500v1 flashing driver for Arduino Nano (Optiboot, 115200 baud)
// Uses Web Serial API directly.

const CMD = {
    GET_SYNC: 0x30,
    GET_SIGN_ON: 0x31,
    SET_PARAMETER: 0x40,
    GET_PARAMETER: 0x41,
    ENTER_PROGMODE: 0x50,
    LEAVE_PROGMODE: 0x51,
    LOAD_ADDRESS: 0x55,
    PROG_PAGE: 0x64,
    READ_PAGE: 0x74,
    READ_SIGN: 0x75,
    CRC_EOP: 0x20,
};

const RESP = {
    INSYNC: 0x14,
    OK: 0x10,
};

const PAGE_SIZE = 128; // ATmega328P flash page = 64 words = 128 bytes

export function isWebSerialSupported() {
    return typeof navigator !== "undefined" && "serial" in navigator;
}

export async function requestPort() {
    if (!isWebSerialSupported()) {
        throw new Error("Web Serial API not supported in this browser. Use Chrome, Edge, or Opera.");
    }
    const port = await navigator.serial.requestPort();
    return port;
}

// The browser only returns devices the user has already approved. This lets us
// reconnect to a single known device without showing the picker every visit.
export async function getAuthorizedPorts() {
    if (!isWebSerialSupported()) return [];
    return navigator.serial.getPorts();
}

/**
 * Open a requested serial port for plain-text serial monitoring.
 * The returned session must be closed before the port can be flashed again.
 */
export async function startSerialLogger(port, { baudRate = 115200, onData, onStatus } = {}) {
    if (!port) throw new Error("Select a device before starting the serial logger.");

    const report = onStatus || (() => {});
    const emit = onData || (() => {});
    let reader = null;
    let stopped = false;
    const decoder = new TextDecoder();

    await port.open({
        baudRate,
        dataBits: 8,
        stopBits: 1,
        parity: "none",
        flowControl: "none",
    });
    report(`Serial logger opened @ ${baudRate} baud`);

    const readLoop = (async () => {
        try {
            reader = port.readable.getReader();
            while (!stopped) {
                const { value, done } = await reader.read();
                if (done) break;
                if (value?.length) emit(decoder.decode(value, { stream: true }));
            }
            const tail = decoder.decode();
            if (tail) emit(tail);
        } catch (error) {
            if (!stopped) report(`Serial logger stopped: ${error.message || error}`);
        } finally {
            try {
                reader?.releaseLock();
            } catch {}
        }
    })();

    return {
        async close() {
            if (stopped) return;
            stopped = true;
            try {
                await reader?.cancel();
            } catch {}
            await readLoop;
            try {
                await port.close();
            } catch {}
            report("Serial logger closed");
        },
    };
}

function delay(ms) {
    return new Promise((res) => setTimeout(res, ms));
}

class PortIO {
    constructor(port) {
        this.port = port;
        this.reader = null;
        this.writer = null;
        this.buffer = [];
        this.closed = false;
        this._readLoop = null;
    }

    async open(baudRate = 115200) {
        await this.port.open({
            baudRate,
            dataBits: 8,
            stopBits: 1,
            parity: "none",
            flowControl: "none",
        });
        this.writer = this.port.writable.getWriter();
        this.reader = this.port.readable.getReader();
        this._readLoop = this._startReadLoop();
    }

    async _startReadLoop() {
        try {
            while (!this.closed) {
                const { value, done } = await this.reader.read();
                if (done) break;
                if (value) {
                    for (let i = 0; i < value.length; i++) {
                        this.buffer.push(value[i]);
                    }
                }
            }
        } catch (e) {
            // reader cancelled or port closed
        }
    }

    async setSignals({ dtr, rts }) {
        await this.port.setSignals({
            dataTerminalReady: dtr,
            requestToSend: rts,
        });
    }

    async write(bytes) {
        const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
        await this.writer.write(data);
    }

    async readBytes(n, timeoutMs = 1000) {
        const start = performance.now();
        while (this.buffer.length < n) {
            if (performance.now() - start > timeoutMs) {
                throw new Error(
                    `Timeout: expected ${n} bytes, got ${this.buffer.length}`,
                );
            }
            await delay(5);
        }
        return this.buffer.splice(0, n);
    }

    flushInput() {
        this.buffer = [];
    }

    async close() {
        this.closed = true;
        try {
            if (this.reader) {
                await this.reader.cancel();
                this.reader.releaseLock();
            }
        } catch {}
        try {
            if (this.writer) {
                await this.writer.close().catch(() => {});
                this.writer.releaseLock();
            }
        } catch {}
        try {
            await this.port.close();
        } catch {}
    }
}

async function expectResponse(io, cmdName) {
    const resp = await io.readBytes(2, 2000);
    if (resp[0] !== RESP.INSYNC) {
        throw new Error(
            `${cmdName}: expected INSYNC (0x14), got 0x${resp[0].toString(16).padStart(2, "0")}`,
        );
    }
    if (resp[1] !== RESP.OK) {
        throw new Error(
            `${cmdName}: expected OK (0x10), got 0x${resp[1].toString(16).padStart(2, "0")}`,
        );
    }
}

async function sendCommand(io, bytes, cmdName) {
    io.flushInput();
    await io.write(new Uint8Array(bytes));
    await expectResponse(io, cmdName);
}

async function sync(io, log) {
    let lastErr = null;
    for (let attempt = 1; attempt <= 5; attempt++) {
        try {
            io.flushInput();
            await io.write(new Uint8Array([CMD.GET_SYNC, CMD.CRC_EOP]));
            const resp = await io.readBytes(2, 500);
            if (resp[0] === RESP.INSYNC && resp[1] === RESP.OK) {
                log("ok", `Sync acquired (attempt ${attempt})`);
                return true;
            }
            throw new Error(
                `bad sync response 0x${resp[0].toString(16)} 0x${resp[1].toString(16)}`,
            );
        } catch (e) {
            lastErr = e;
            log("info", `Sync attempt ${attempt} failed: ${e.message}`);
            await delay(100);
        }
    }
    throw new Error(`Failed to sync with bootloader: ${lastErr?.message}`);
}

/**
 * Flash a Uint8Array buffer to Arduino Nano via STK500v1 / Optiboot.
 * @param {SerialPort} port - Web Serial port (already requested by user)
 * @param {Uint8Array} firmware - full firmware image (byte-addressed, starting at 0)
 * @param {object} opts
 * @param {(pct:number, page:number, total:number)=>void} opts.onProgress
 * @param {(level:string, msg:string)=>void} opts.onLog
 */
export async function flashNano(port, firmware, { onProgress, onLog, baudRate = 115200 }) {
    const log = onLog || (() => {});
    const progress = onProgress || (() => {});
    const io = new PortIO(port);

    try {
        log("info", `Opening serial port @ ${baudRate} 8N1…`);
        await io.open(baudRate);
        log("ok", "Port opened");

        // Reset the Nano via DTR/RTS toggle (auto-reset circuit)
        log("info", "Resetting board (DTR/RTS toggle)…");
        await io.setSignals({ dtr: false, rts: false });
        await delay(150);
        await io.setSignals({ dtr: true, rts: true });
        await delay(50);
        await io.setSignals({ dtr: false, rts: false });
        // Bootloader is alive for ~1s on new Nano bootloader
        await delay(50);
        io.flushInput();

        // Sync
        log("tx", "→ STK_GET_SYNC (0x30 0x20)");
        await sync(io, log);

        // Enter programming mode
        log("tx", "→ STK_ENTER_PROGMODE (0x50 0x20)");
        await sendCommand(io, [CMD.ENTER_PROGMODE, CMD.CRC_EOP], "ENTER_PROGMODE");
        log("rx", "← INSYNC OK");

        // Pad firmware to page boundary
        const padded = new Uint8Array(Math.ceil(firmware.length / PAGE_SIZE) * PAGE_SIZE);
        padded.fill(0xff);
        padded.set(firmware);

        const totalPages = padded.length / PAGE_SIZE;
        log("info", `Firmware size: ${firmware.length} bytes → ${totalPages} pages (${PAGE_SIZE}B each)`);

        for (let p = 0; p < totalPages; p++) {
            const byteAddr = p * PAGE_SIZE;
            const wordAddr = byteAddr >> 1; // STK500 uses word addresses for flash

            // LOAD_ADDRESS (low byte first)
            const loadCmd = [
                CMD.LOAD_ADDRESS,
                wordAddr & 0xff,
                (wordAddr >> 8) & 0xff,
                CMD.CRC_EOP,
            ];
            await sendCommand(io, loadCmd, `LOAD_ADDRESS page ${p + 1}`);

            // PROG_PAGE
            const pageData = padded.slice(byteAddr, byteAddr + PAGE_SIZE);
            const progCmd = new Uint8Array(5 + PAGE_SIZE);
            progCmd[0] = CMD.PROG_PAGE;
            progCmd[1] = (PAGE_SIZE >> 8) & 0xff; // size high
            progCmd[2] = PAGE_SIZE & 0xff; // size low
            progCmd[3] = 0x46; // 'F' = flash
            progCmd.set(pageData, 4);
            progCmd[4 + PAGE_SIZE] = CMD.CRC_EOP;

            io.flushInput();
            await io.write(progCmd);
            await expectResponse(io, `PROG_PAGE ${p + 1}`);

            const pct = Math.round(((p + 1) / totalPages) * 100);
            progress(pct, p + 1, totalPages);
            if ((p + 1) % 8 === 0 || p === totalPages - 1) {
                log(
                    "tx",
                    `→ Flashed page ${p + 1}/${totalPages}  @ 0x${byteAddr.toString(16).padStart(4, "0")}  (${pct}%)`,
                );
            }
        }

        // Leave programming mode
        log("tx", "→ STK_LEAVE_PROGMODE (0x51 0x20)");
        await sendCommand(io, [CMD.LEAVE_PROGMODE, CMD.CRC_EOP], "LEAVE_PROGMODE");
        log("rx", "← INSYNC OK");

        log("ok", `Flash complete — ${firmware.length} bytes written in ${totalPages} pages`);

        // Optional reset so sketch starts running
        await io.setSignals({ dtr: true, rts: true });
        await delay(50);
        await io.setSignals({ dtr: false, rts: false });
        log("info", "Board reset — sketch is now running");
    } finally {
        await io.close();
        log("info", "Port closed");
    }
}
